import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const empty = { code: "", discount_percent: 10, min_order: 0, description: "", active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/coupons", { withCredentials: true }).then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const del = async (code) => {
    if (!confirm(`Delete coupon ${code}?`)) return;
    try { await api.delete(`/admin/coupons/${code}`, { withCredentials: true }); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const save = async (e) => {
    e.preventDefault();
    const isNew = !coupons.find((c) => c.code === editing.code);
    try {
      const payload = { ...editing, code: editing.code.toUpperCase(), discount_percent: Number(editing.discount_percent), min_order: Number(editing.min_order) };
      if (isNew) await api.post("/admin/coupons", payload, { withCredentials: true });
      else await api.put(`/admin/coupons/${payload.code}`, payload, { withCredentials: true });
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-8">
        <div><p className="label-tiny text-brand-gold">Promotions</p><h1 className="font-serif-brand text-4xl text-brand-charcoal mt-1">Coupons · {coupons.length}</h1></div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary flex items-center gap-2" data-testid="new-coupon-btn"><Plus size={14} /> New Coupon</button>
      </div>

      <div className="border border-brand-gold/25 bg-brand-ivory overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-ivory-soft">
            <tr className="text-left label-tiny text-brand-muted">
              <th className="p-4">Code</th><th>Discount</th><th>Min Order</th><th>Description</th><th>Active</th><th></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.code} className="border-t border-brand-gold/15" data-testid={`coupon-row-${c.code}`}>
                <td className="p-4 font-medium text-brand-maroon">{c.code}</td>
                <td className="p-4">{c.discount_percent}%</td>
                <td className="p-4">₹{c.min_order}</td>
                <td className="p-4 text-brand-muted">{c.description}</td>
                <td className="p-4">{c.active ? "✔" : "—"}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setEditing(c)} className="p-2 text-brand-charcoal hover:text-brand-maroon"><Edit2 size={14} /></button>
                  <button onClick={() => del(c.code)} className="p-2 text-brand-charcoal hover:text-brand-maroon"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/60 flex items-start justify-center p-4 lg:p-10" onClick={() => setEditing(null)} data-testid="coupon-form">
          <form onSubmit={save} className="bg-brand-ivory w-full max-w-md p-8 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-serif-brand text-2xl text-brand-charcoal">{editing.code ? "Edit" : "New"} Coupon</h2><button type="button" onClick={() => setEditing(null)}><X /></button></div>
            <label className="block"><span className="label-tiny text-brand-muted">Code</span>
              <input required value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon uppercase" data-testid="coupon-code-input" /></label>
            <label className="block"><span className="label-tiny text-brand-muted">Discount %</span>
              <input required type="number" min={1} max={100} value={editing.discount_percent} onChange={(e) => setEditing({ ...editing, discount_percent: e.target.value })} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>
            <label className="block"><span className="label-tiny text-brand-muted">Minimum Order ₹</span>
              <input required type="number" min={0} value={editing.min_order} onChange={(e) => setEditing({ ...editing, min_order: e.target.value })} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>
            <label className="block"><span className="label-tiny text-brand-muted">Description</span>
              <input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full mt-1 border border-brand-gold/40 px-3 py-2 outline-none focus:border-brand-maroon" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
            <button type="submit" className="btn-primary w-full" data-testid="save-coupon-btn">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
